import { Box, Link, Typography } from "@mui/material";
import Grid from "@mui/material/Grid/Grid";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext } from "react";
import { Plans } from "schemas/payments";

import ContactListItem from "features/Influencer/QuickSendIndividualDrawer/PreviewPerContact/PreviewIndividual/ContactListItem";
import { trackEvent } from "utils/tracking";
import { useSubscription } from "utils/useSubscription";

export default function UpgradeFeatureDescription() {
  const {
    upgradeDialogSource,
    subscription,
    recommendationsRefreshed,
    brandRequestsMade,
  } = useContext(SubscriptionContext);
  const { handleCheckout } = useSubscription();

  if (upgradeDialogSource === "Hits Limit") {
    return (
      <Typography paragraph>
        Your current plan only allows you to send{" "}
        <strong>{subscription?.monthlyLimit} emails</strong> per month but you
        are attempting to send more than that. Please upgrade.
      </Typography>
    );
  } else if (upgradeDialogSource === "Hits Campaign Limit") {
    return (
      <Typography paragraph>
        You can only send{" "}
        <Box component="span" sx={{ color: "info.main" }}>
          <strong>{subscription?.campaignLimit}</strong>
        </Box>{" "}
        emails per campaign. Please upgrade to send more or change the number of
        brands selected in this campaign.
      </Typography>
    );
  } else if (upgradeDialogSource === "Hits Brand Request Limit") {
    return (
      <Typography paragraph>
        You have made{" "}
        <Box component="span" sx={{ color: "info.main" }}>
          <strong>{brandRequestsMade}</strong>
        </Box>{" "}
        brand requests. Please upgrade to make additional requests.
      </Typography>
    );
  } else if (upgradeDialogSource === "Hits Refreshing Recommendations Limit") {
    return (
      <Typography paragraph>
        You have refreshed your recommendations{" "}
        <Box component="span" sx={{ color: "info.main" }}>
          <strong>{recommendationsRefreshed}</strong>
        </Box>{" "}
        times this month. Please upgrade to refresh.
      </Typography>
    );
  } else if (upgradeDialogSource === "Exclusive Contact") {
    return (
      <Typography paragraph textAlign="center">
        <Box
          component="i"
          className="fa-regular fa-circle-info"
          sx={{ mr: 1, color: "info.main" }}
        />
        This is <strong>an exclusive contact</strong> (who is 2-3 times more
        likely respond). You need to upgrade to email them.
      </Typography>
    );
  } else if (upgradeDialogSource === "Access Partnership Data") {
    return (
      <Typography paragraph>
        To gain access to this exclusive brand and contact partnership data,
        subscribe to our Partnership Data add-on or upgrade to our{" "}
        <Link
          sx={{ textDecoration: "none", cursor: "pointer" }}
          onClick={() => {
            trackEvent("Virtual Assistant Plan Clicked From Partnership Data");
            handleCheckout(Plans.VA);
          }}
        >
          <strong>Virtual Assistant</strong>
        </Link>{" "}
        plan.
      </Typography>
    );
  } else if (upgradeDialogSource === "Reply Score") {
    return (
      <Grid container justifyContent="center" sx={{ mb: 3 }}>
        <Typography sx={{ mb: 2 }} component="div">
          The <strong>Reply Score</strong> feature shows more information about
          brand contacts such as: <br />
          <ContactListItem
            isActive
            text="If contact is email verified (not bounced)"
          />
          <ContactListItem
            isActive
            text="If contact replied to a user on platform"
          />
          <ContactListItem
            isActive
            text="If contact discussed a specific opportunity with a user"
          />
        </Typography>
        <Typography textAlign="center" component="div">
          To gain access to this feature, purchase Partnership Data add-on or
          upgrade to our{" "}
          <Link
            sx={{ textDecoration: "none", cursor: "pointer" }}
            onClick={() => {
              trackEvent(
                "Virtual Assistant Plan Clicked From Partnership Data",
              );
              handleCheckout(Plans.VA);
            }}
          >
            <strong>Virtual Assistant</strong>
          </Link>{" "}
          plan.
        </Typography>
      </Grid>
    );
  } else if (upgradeDialogSource === "Restricted Filtering Feature") {
    return (
      <Grid container justifyContent="center" sx={{ mb: 3 }}>
        <Typography gutterBottom>
          The <strong>Brand Filtering</strong> feature gives you the ability to
          filter for brands that have
        </Typography>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          item
          xs={12}
        >
          <Box
            component="i"
            className="fa-solid fa-briefcase fa-fw"
            sx={{ color: "#32885F", fontSize: 12, mr: 1 }}
          />
          <Typography gutterBottom>Partners With Creators</Typography>
        </Grid>
        <Grid
          justifyContent="center"
          container
          alignItems="center"
          item
          xs={12}
        >
          <Box
            component="i"
            className="fa-solid fa-tag fa-fw"
            sx={{ color: "#388BED", fontSize: 12, mr: 1 }}
          />
          <Typography gutterBottom>Works with UGC creators</Typography>
        </Grid>
        <Grid
          justifyContent="center"
          container
          alignItems="center"
          item
          xs={12}
        >
          <Box
            component="i"
            className="fa-solid fa-hashtag fa-fw"
            sx={{ color: "#E68FE4", fontSize: 12, mr: 1 }}
          />
          <Typography gutterBottom>Works with Influencers</Typography>
        </Grid>
        <Grid
          justifyContent="center"
          container
          alignItems="center"
          item
          xs={12}
        >
          <Box
            component="i"
            className="fa-brands fa-tiktok fa-fw"
            sx={{ color: "#010101", fontSize: 12, mr: 1 }}
          />
          <Typography gutterBottom>Runs TikTok Ads</Typography>
        </Grid>

        <Typography gutterBottom>
          This will help you speed up your brand search while spending time
          pitching to the right brands.
        </Typography>
      </Grid>
    );
  } else {
    return <></>;
  }
}
